<template>
  <div class="gw-steps-wrapper">
    <div class="steps-content f-h f-w">
      <div
        v-for="(step, ind) in gw.steps"
        :key="step.id"
        class="step-item generic-border generic-border--thin-border generic-border--clickable"
        :class="{ 'generic-border--focused': ind == index }"
      >
        <div class="step-item__main-content" @click="stepClicked(ind)">
          <div class="step-item__text">
            <EllipsisDiv :key="`${ind}-${stepNameKeyModfier}`" class="step-item__name" :text="decodeHTML(step.name)" />
            <div class="step-item__duration v-centerer">
              <Icon>{{ clockMedium }}</Icon>
              <span>{{ `${$t('message.DURATION')}${$t('message.TWO_DOTS')}${duration(step)}` }}</span>
            </div>
          </div>
          <div class="step-item__icons">
            <div v-if="nProblemsPerStep[`${step.id}`]" class="step-item__problems v-centerer">
              <span>{{ nProblemsPerStep[`${step.id}`] }}</span>
              <Icon>{{ problemsMedium }}</Icon>
            </div>
            <div class="step-item__ticker h-v-centerer" @click="completed ? okStep($event, step) : null">
              <span v-if="step.ok == 2">●</span>
              <Icon v-else-if="step.ok == 1" :color="SColors.green">{{ okFill }}</Icon>
              <Icon v-else :color="SColors.redBad">{{ nokFill }}</Icon>
            </div>
          </div>
        </div>

        <TransitionExpand>
          <div v-if="completed && ind === index" class="gw-step__transition-container">
            <div class="sep" />
            <div class="gw-step-data">
              <!--  Expandable comments  -->
              <ExpandableSettingsItem
                :title="$t('message.GW_DISPLAY_COMMENTS')"
                use-transition-expand
              >
                <GwCommentsWrapper
                  :gw="gw"
                  :step-id="currentStep.id"
                  :comments="currentStep.comments"
                  :pb-button="true"
                  :disabled="disabled"
                  @input="updateCurrentStep"
                  @reportProblem="reportProblem"
                />
              </ExpandableSettingsItem>
              <!--  Expandable checkLists  -->
              <ExpandableSettingsItem
                :html-id="`step-checklist-${currentStep.id}`"
                :title="$t('message.GW_DISPLAY_CHECKLISTS')"
                use-transition-expand
              >
                <GwChecklistsWrapper
                  :gw="gw"
                  :step-index="ind"
                  :check-lists="step.checklists"
                  :parent-ids="['gw-data' + ind]"
                  :disabled="disabled"
                  @edit="emitEdit"
                />
              </ExpandableSettingsItem>
            </div>
          </div>
        </TransitionExpand>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Use for gemba walk's step navigation
 *
 * @vue-prop {Object} gw - GW data related to the step
 * @vue-prop {Number} index - Index of the step in the gemba walk
 * @vue-prop {Boolean} completed - Is the gemba walk completed or not
 * @vue-prop {Boolean} disabled - Should the step be editable or not
 *
 * @vue-event {Number, Number} stepChange - Emit current step and previous step index after step change
 * @vue-event {Number, Object} reportProblem - Emit comment data for problem creation
 * @vue-event edit - Triggered when a step is edited
 */

import $ from 'jquery';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Icon from '@/components/icon.vue';
import GwChecklistsWrapper from '@/components/gw-checklists-wrapper.vue';
import GwCommentsWrapper from '@/components/gw-comments-wrapper.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Utils
import { itemExpand } from '@/utils/expandable';
import { decodeHTML, secondsToArray } from '@/utils/main';
import { SColors } from '@/utils/constants';
import { clockMedium, nokFill, okFill, problemsMedium } from '@/assets/icons/';
// Vuex utils
import { VALIDATE_STEP } from '@/store/modules/gemba-walks/action-types';

export default {

  name: 'GwStepsNavigator',

  components: {
    EllipsisDiv,
    ExpandableSettingsItem,
    Icon,
    GwChecklistsWrapper,
    GwCommentsWrapper,
    TransitionExpand,
  },

  props: {
    gw: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    completed: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      clockMedium,
      nokFill,
      okFill,
      problemsMedium,
      SColors,
      firstLoad: true,
      stepNameKeyModfier: 1,
    };
  },

  computed: {
    currentStep() {
      if (this.gw && this.gw.steps && this.gw.steps.length) {
        return this.gw.steps[this.index];
      }
      return { name: '', prevComments: [] };
    },
    /** Object: { <id_of_step>: <number_of_problems> } */
    nProblemsPerStep() {
      const nProblemsPerStep = {};
      (this.gw.steps || []).forEach((step) => {
        nProblemsPerStep[`${step.id}`] = 0;
        (step.checklists || []).forEach((checklist) => {
          nProblemsPerStep[`${step.id}`] += checklist.items.reduce((n, item) => n + item.problems.length, 0);
        });
      });
      return nProblemsPerStep;
    },
  },

  watch: {
    currentStep() {
      if (this.firstLoad && this.completed && this.currentStep && this.currentStep.id) {
        this.firstLoad = false;
        this.updateCurrentStep();
      }
    },
  },

  mounted() {
    $(window).on(this.resizeEventName, this.updateCurrentStep);
  },

  methods: {
    decodeHTML,
    itemExpand,
    reportProblem(...args) {
      this.$emit('reportProblem', ...args);
    },
    /** Refresh step name components */
    updateNames() {
      this.stepNameKeyModfier++;
    },
    /** Click on a step to get back (or go forward) to it */
    stepClicked(index) {
      if (Number(index) !== Number(this.index)) {
        const prevIndex = this.index;
        this.$emit('stepChange', index, prevIndex);
        this.updateCurrentStep();
        this.$nextTick(() => {
          setTimeout(this.updateNames, 300);
        });
      }
    },
    /** Refresh overflow (call itemExpand) and comments data */
    updateCurrentStep(comments) {
      if (comments && comments instanceof Array) {
        this.currentStep.comments = comments;
      }
    },
    emitEdit() {
      this.updateCurrentStep();
      this.$emit('edit');
    },
    subtitle(forStep) {
      let out = '';
      let len;
      if (forStep.comments && Array.isArray(forStep.comments)) {
        len = forStep.comments.length;
        out = this.$tc('message.GW_N_COMMENTS', len, { n: len });
      }
      if (forStep.problems && Array.isArray(forStep.problems)) {
        if (typeof len !== 'undefined') {
          out += ' - ';
        }
        len = forStep.problems.length;
        forStep.checklists.forEach((cl) => {
          cl.items.forEach((item) => {
            len += item.problems.length;
          });
        });
        out += this.$tc('message.GW_N_PROBLEMS', len, { n: len });
      }
      if (forStep.actions && Array.isArray(forStep.actions)) {
        if (typeof len !== 'undefined') {
          out += ' - ';
        }
        len = forStep.actions.length;
        out += this.$tc('message.GW_N_ACTIONS', len, { n: len });
      }
      return out;
    },
    duration(step) {
      const duration = Number(step.duration || 0);
      const time = secondsToArray(duration);
      return `${time[0]}:${time[1]}:${time[2]}`;
    },
    /** Validate / Unvalidate the current step */
    okStep(e, step) {
      if (!this.completed || this.disabled) {
        return;
      }
      e.stopPropagation();
      const ok = (Number(step.ok) + 1) % 3;
      const params = { gwId: this.gw.id, id: step.id, ok, duration: step.duration };
      this.$store.dispatch(`gembaWalks/${VALIDATE_STEP}`, params);
    },
    /** Number of OK items in all checklists */
    nOK(step) {
      return this.nItemsWithState(step, 1);
    },
    /** Number of NOK items in all checklists */
    nNOK(step) {
      return this.nItemsWithState(step, 0);
    },
    /** Number of checklists items with given state */
    nItemsWithState(step, state) {
      let cl;
      let j;
      let jlen;
      let nb = 0;
      if (step.checklists && step.checklists.length) {
        for (let i = 0, len = step.checklists.length; i < len; i++) {
          cl = step.checklists[i];
          for (j = 0, jlen = cl.items.length; j < jlen; j++) {
            if (Number(cl.items[j].ok) === Number(state)) {
              nb++;
            }
          }
        }
      }
      return nb;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-steps-wrapper {
  height: calc(100% - 25px);

  .steps-content {
    height: calc(100% - 10px)
  }

  .step-item {
    cursor: pointer;
    background: #ffffff;
    border: 1px solid $color-gray-border;
    border-radius: $main-border-radius;

    &.generic-border--thin-border {
      padding: 0px;
    }

    &:not(:first-child) {
      margin-top: 10px;
    }

    &__main-content {
      padding: 0;
      display: flex;
    }

    &__text {
      flex: 1;
      padding: .7rem 0 .7rem 1rem;
      min-width: 0px;
    }

    &__icons {
      flex: 0 0 auto;
      padding: .5rem .5rem .5rem 1rem;
      align-items: flex-end;
      justify-content: end;
      display: flex;
      flex-flow: column;
    }

    &__duration {
      margin-top: .5rem;
      display: inline-flex;
      font-size: 0.85rem;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      .icon {
        width: .9rem;
        height: .9rem;
        margin-right: .5rem;
      }
    }

    &__name {
      height: 1.5em;
      overflow-y: hidden;
      font-weight: 600;
    }

    &__ticker {
      width: 1.2rem;
      height: 1.2rem;
      padding: .5rem;
      margin-top: 0.3rem;
    }

    &__problems {
      .icon {
        width: .85rem;
        height: .85rem;
        margin-left: 0.2rem;
      }
    }
  }

  .gw-step__transition-container .sep {
    margin: 0.7rem 0;
    width: initial;
  }

  .gw-step-data {
    padding: 0px 10px;
    overflow: hidden;
  }
  .gw-step-data .expander img {
    height: 20px;
    width: auto;
  }
}
</style>
